import { useState, useEffect } from 'react'
import { useAuthStore } from '../store/authStore'
import { useEvidenceStore } from '../store/evidenceStore'
import SecureFileViewer from '../components/SecureFileViewer'
import UploadEvidenceForm from '../components/UploadEvidenceForm'
import CriterioSelectorView from '../components/CriterioSelectorView'
import { Upload, FileText, Trash2, Calendar, Download, FolderTree } from 'lucide-react'
import DownloadZipButton from '../components/DownloadZipButton'
import { format } from 'date-fns'
import { es } from 'date-fns/locale'

const UserDashboard = () => {
  const [activeTab, setActiveTab] = useState('upload')
  const [selectedEvidences, setSelectedEvidences] = useState([])
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [viewerFile, setViewerFile] = useState(null)
  const [showViewer, setShowViewer] = useState(false)
  
  const { user, token } = useAuthStore()
  const { evidences, fetchUserEvidences, deleteEvidences, isLoading, error } = useEvidenceStore()

  useEffect(() => {
    fetchUserEvidences(token)
  }, [token, fetchUserEvidences])

  const handleSelectEvidence = (evidenceId) => {
    setSelectedEvidences(prev => 
      prev.includes(evidenceId)
        ? prev.filter(id => id !== evidenceId)
        : [...prev, evidenceId]
    )
  }

  const handleSelectAll = () => {
    if (selectedEvidences.length === evidences.length) {
      setSelectedEvidences([])
    } else {
      setSelectedEvidences(evidences.map(e => e.id))
    }
  }

  const handleDeleteSelected = async () => {
    if (selectedEvidences.length === 0) return
    
    const result = await deleteEvidences(token, selectedEvidences)
    if (result.success) {
      setSelectedEvidences([])
      setShowDeleteModal(false)
    }
  }

  // Agrupar evidencias por criterio
  const evidencesByCriterio = evidences.reduce((acc, evidence) => {
    const criterio = evidence.criterio
    if (!acc[criterio]) {
      acc[criterio] = []
    }
    acc[criterio].push(evidence)
    return acc
  }, {})

  const tabs = [
    { id: 'upload', label: 'Subir Evidencia', icon: Upload },
    { id: 'evidences', label: 'Mis Evidencias', icon: FileText },
    { id: 'criterios', label: 'Mejora Continua', icon: FolderTree },
  ]

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Panel de Usuario</h1>
          <p className="text-gray-600">Programa: {user?.programa}</p>
        </div>
        
        <div className="flex items-center space-x-4">
          <div className="bg-white rounded-lg p-4 shadow-sm">
            <div className="text-sm text-gray-600">Total de evidencias</div>
            <div className="text-2xl font-bold text-blue-600">{evidences.length}</div>
          </div>
          
          <a
            href="/brechas-test"
            className="btn-primary inline-flex items-center space-x-2"
          >
            <FolderTree className="h-4 w-4" />
            <span>🧪 Probar Brechas (Fase 1)</span>
          </a>
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200">
        <nav className="-mb-px flex space-x-8">
          {tabs.map((tab) => {
            const Icon = tab.icon
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center space-x-2 py-2 px-1 border-b-2 font-medium text-sm ${
                  activeTab === tab.id
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <Icon className="h-4 w-4" />
                <span>{tab.label}</span>
              </button>
            )
          })}
        </nav>
      </div>

      {/* Tab Content */}
      {activeTab === 'upload' && <UploadEvidenceForm />}

      {activeTab === 'evidences' && (
        <div className="space-y-6">
          {/* Controles */}
          <div className="card">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-4">
                <label className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    checked={selectedEvidences.length === evidences.length && evidences.length > 0}
                    onChange={handleSelectAll}
                    className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  />
                  <span className="text-sm text-gray-700">
                    Seleccionar todas ({evidences.length})
                  </span>
                </label>
                
                {selectedEvidences.length > 0 && (
                  <span className="text-sm text-blue-600">
                    {selectedEvidences.length} seleccionadas
                  </span>
                )}
              </div>

              <div className="flex items-center space-x-3">
                {/* Botón de descarga ZIP */}
                <DownloadZipButton 
                  selectedEvidences={selectedEvidences}
                  allEvidences={evidences}
                  filters={{}}
                />

                {selectedEvidences.length > 0 && (
                  <button
                    onClick={() => setShowDeleteModal(true)}
                    className="btn-danger flex items-center space-x-2"
                  >
                    <Trash2 className="h-4 w-4" />
                    <span>Eliminar Seleccionadas</span>
                  </button>
                )}
              </div>
            </div>
          </div>

          {/* Lista de evidencias agrupadas */}
          {isLoading ? (
            <div className="text-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
              <p className="mt-2 text-gray-600">Cargando evidencias...</p>
            </div>
          ) : Object.keys(evidencesByCriterio).length === 0 ? (
            <div className="card text-center py-8">
              <FileText className="mx-auto h-12 w-12 text-gray-400 mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">
                No hay evidencias
              </h3>
              <p className="text-gray-600">
                Comienza subiendo tu primera evidencia en la pestaña "Subir Evidencia"
              </p>
            </div>
          ) : (
            <div className="space-y-6">
              {Object.entries(evidencesByCriterio).map(([criterio, criterioEvidences]) => (
                <div key={criterio} className="card">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">
                    {criterio}
                  </h3>
                  
                  <div className="space-y-3">
                    {criterioEvidences.map((evidence) => (
                      <div
                        key={evidence.id}
                        className="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
                      >
                        <div className="flex items-center space-x-3">
                          <input
                            type="checkbox"
                            checked={selectedEvidences.includes(evidence.id)}
                            onChange={() => handleSelectEvidence(evidence.id)}
                            className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                          />
                          
                          <div className="flex-1">
                            <div className="flex items-center space-x-2">
                              <FileText className="h-4 w-4 text-gray-500" />
                              <span className="font-medium text-gray-900">
                                {evidence.nombre_archivo}
                              </span>
                            </div>
                            
                            <div className="flex items-center space-x-4 mt-1 text-sm text-gray-600">
                              <div className="flex items-center space-x-1">
                                <Calendar className="h-3 w-3" />
                                <span>
                                  {format(new Date(evidence.fecha_hora), 'dd/MM/yyyy HH:mm', { locale: es })}
                                </span>
                              </div>
                              <span>{evidence.dimension}</span>
                            </div>
                          </div>
                        </div>

                        <div className="flex items-center space-x-2">
                          {evidence.url_archivo ? (
                            <button
                              onClick={() => {
                                setViewerFile({
                                  url: evidence.url_archivo,
                                  name: evidence.nombre_archivo
                                })
                                setShowViewer(true)
                              }}
                              className="inline-flex items-center space-x-1 px-3 py-1 bg-blue-100 text-blue-700 hover:bg-blue-200 rounded-md transition-colors duration-200"
                            >
                              <Download className="h-4 w-4" />
                              <span>Descargar</span>
                            </button>
                          ) : (
                            <span className="inline-flex items-center space-x-1 px-3 py-1 bg-gray-100 text-gray-500 rounded-md">
                              <FileText className="h-4 w-4" />
                              <span>Sin archivo</span>
                            </span>
                          )}
                          
                          <button
                            onClick={() => {
                              setSelectedEvidences([evidence.id])
                              setShowDeleteModal(true)
                            }}
                            className="inline-flex items-center space-x-1 px-3 py-1 bg-red-100 text-red-700 hover:bg-red-200 rounded-md transition-colors duration-200"
                          >
                            <Trash2 className="h-4 w-4" />
                            <span>Eliminar</span>
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Tab Por Criterio */}
      {activeTab === 'criterios' && (
        <CriterioSelectorView />
      )}

      {/* Modal de confirmación de eliminación */}
      {showDeleteModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              Confirmar Eliminación
            </h3>
            <p className="text-gray-600 mb-6">
              ¿Estás seguro de que deseas eliminar {selectedEvidences.length} evidencia(s)? 
              Esta acción no se puede deshacer.
            </p>
            
            <div className="flex space-x-3">
              <button
                onClick={() => setShowDeleteModal(false)}
                className="btn-secondary flex-1"
              >
                Cancelar
              </button>
              <button
                onClick={handleDeleteSelected}
                disabled={isLoading}
                className="btn-danger flex-1 disabled:opacity-50"
              >
                {isLoading ? 'Eliminando...' : 'Eliminar'}
              </button>
            </div>
          </div>
        </div>
      )}

      {error && (
        <div className="p-4 bg-red-100 border border-red-400 text-red-700 rounded">
          {error}
        </div>
      )}

      {/* Visualizador de archivos */}
      <SecureFileViewer
        fileName={viewerFile}
        isOpen={showViewer}
        onClose={() => {
          setShowViewer(false)
          setViewerFile(null)
        }}
      />
    </div>
  )
}

export default UserDashboard